import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing } from 'lucide-react';
import type { Appointment } from '@tailoring-crm/shared';
import { formatTime } from '../../lib/format.js';
import { Button, cn } from '@tailoring-crm/ui';

/**
 * One booking, drawn inside its cell.
 *
 * Opening it — a tap on the summary — is what exposes the three actions, rather than the
 * buttons sitting there always: a half-hour row is narrow, and a cell showing three buttons for
 * every appointment on the grid at once would be unreadable long before the second column.
 *
 * ONLY A `scheduled` APPOINTMENT OFFERS THEM AT ALL. `completed` and `cancelled` are both
 * terminal — see the module note on the shared schema — and a button that looked actionable on
 * a closed slot would be a promise the screen cannot keep.
 */
export function AppointmentCell({
  appointment,
  onReschedule,
  onCancel,
  onMarkComplete,
}: {
  appointment: Appointment;
  onReschedule: (appointment: Appointment) => void;
  onCancel: (appointment: Appointment) => void;
  onMarkComplete: (appointment: Appointment) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const canAct = appointment.status === 'scheduled';

  return (
    <div
      data-testid="appointment-cell"
      className={cn(
        'flex h-full flex-col gap-1 rounded-md border border-border bg-card p-1.5 text-left text-xs',
        appointment.status === 'cancelled' && 'border-dashed opacity-60',
        appointment.status === 'completed' && 'border-success/40 bg-success/10',
      )}
    >
      <button
        type="button"
        className="flex flex-col gap-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => canAct && setExpanded((previous) => !previous)}
        aria-expanded={canAct ? expanded : undefined}
      >
        <span className={cn('font-medium', appointment.status === 'cancelled' && 'line-through')}>
          {appointment.customerName}
        </span>
        <span className="text-muted-foreground">{appointment.purpose}</span>
        <span className="text-muted-foreground">
          {formatTime(appointment.startAt)}–{formatTime(appointment.endAt)}
        </span>
        {appointment.staffName ? (
          <span className="text-muted-foreground">{appointment.staffName}</span>
        ) : null}
        <span className="flex items-center gap-1">
          {appointment.status !== 'scheduled' ? (
            <span className="font-medium">{t(`appointments.status.${appointment.status}`)}</span>
          ) : null}
          {appointment.reminderSentAt ? (
            <span className="inline-flex items-center gap-0.5 text-muted-foreground">
              <BellRing aria-hidden="true" className="size-3" />
              {t('appointments.grid.reminderSent')}
            </span>
          ) : null}
        </span>
      </button>

      {expanded && canAct ? (
        <div className="flex flex-wrap gap-1 pt-1">
          <Button size="sm" variant="outline" onClick={() => onReschedule(appointment)}>
            {t('appointments.action.reschedule')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onCancel(appointment)}>
            {t('appointments.action.cancel')}
          </Button>
          <Button size="sm" onClick={() => onMarkComplete(appointment)}>
            {t('appointments.action.markComplete')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default AppointmentCell;
